import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, ImageIcon, CheckCircle, XCircle, FileDown } from "lucide-react";
import { usePendingIssues } from "@/hooks/usePendingIssues";
import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export const PendingIssuesTable = () => {
  const { issues: pendingIssues, loading: issuesLoading } = usePendingIssues();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const exportToPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Add Vietnamese font support
    doc.setFont("helvetica");
    
    // Title
    doc.setFontSize(16);
    doc.text("BÁO CÁO VẤN ĐỀ TỒN ĐỌNG", doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
    
    // Subtitle
    doc.setFontSize(10);
    doc.text(`Ngày xuất: ${new Date().toLocaleDateString('vi-VN')}`, doc.internal.pageSize.getWidth() / 2, 22, { align: 'center' });

    // Prepare table data
    const tableData = pendingIssues.map((issue, index) => {
      const errorContent = issue.notes 
        ? `${issue.item_name}\n=> ${issue.notes}` 
        : issue.item_name;
      
      const resolutionContent = issue.resolution_notes 
        ? `Đã sửa chữa:\n${issue.resolution_notes}` 
        : '';

      const fullContent = resolutionContent 
        ? `${errorContent}\n\n${resolutionContent}`
        : errorContent;

      return [
        index + 1,
        issue.work_unit_name || '',
        `${issue.equipment_code}\n${issue.equipment_name}`,
        fullContent,
        new Date(issue.report_date).toLocaleDateString('vi-VN') + '\n' + (issue.inspector_name || ''),
        issue.is_resolved ? 'Đã xử lý' : 'Chưa xử lý'
      ];
    });

    // Add table
    autoTable(doc, {
      startY: 28,
      head: [['STT', 'Xưởng', 'Thiết bị', 'Nội dung lỗi & Sửa chữa', 'Ngày báo cáo', 'Trạng thái']],
      body: tableData,
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 3,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 35 },
        2: { cellWidth: 45 },
        3: { cellWidth: 80 }, // Wider for error & resolution content
        4: { cellWidth: 35, halign: 'center' },
        5: { cellWidth: 25, halign: 'center' }
      },
      didParseCell: function(data) {
        // Highlight resolved items
        if (data.section === 'body' && data.column.index === 5) {
          const issue = pendingIssues[data.row.index];
          if (issue.is_resolved) {
            data.cell.styles.textColor = [34, 139, 34]; // Green
            data.cell.styles.fontStyle = 'bold';
          } else {
            data.cell.styles.textColor = [220, 53, 69]; // Red
          }
        }
        
        // Highlight resolution notes in content column
        if (data.section === 'body' && data.column.index === 3) {
          const issue = pendingIssues[data.row.index];
          if (issue.resolution_notes) {
            data.cell.styles.fillColor = [240, 255, 240]; // Light green background
          }
        }
      },
      margin: { left: 10, right: 10 }
    });

    // Footer with summary
    const finalY = (doc as any).lastAutoTable.finalY || 28;
    doc.setFontSize(10);
    doc.text(`Tổng số vấn đề: ${pendingIssues.length}`, 15, finalY + 10);
    doc.text(`Đã xử lý: ${pendingIssues.filter(i => i.is_resolved).length}`, 15, finalY + 16);
    doc.text(`Chưa xử lý: ${pendingIssues.filter(i => !i.is_resolved).length}`, 15, finalY + 22);

    // Save PDF
    doc.save(`bao-cao-van-de-ton-dong-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              Bảng vấn đề tồn đọng
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Các lỗi từ báo cáo cần xử lý hoặc đã xử lý trong vòng 7 ngày
            </p>
          </div>
          {pendingIssues.length > 0 && !issuesLoading && (
            <Button
              onClick={exportToPDF}
              variant="outline"
              className="flex items-center gap-2"
            >
              <FileDown className="w-4 h-4" />
              Xuất PDF
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {issuesLoading ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-500">Đang tải dữ liệu...</p>
          </div>
        ) : pendingIssues.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-500">Không có vấn đề tồn đọng</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Xưởng</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Thiết bị</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Hạng mục lỗi</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-700">Hình ảnh</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Ngày báo cáo</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-700">Đã xử lý</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingIssues.map((issue, index) => (
                    <tr 
                      key={issue.id} 
                      className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${issue.is_resolved ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-2">{issue.work_unit_name}</td>
                      <td className="px-4 py-2">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">{issue.equipment_code}</span>
                          <span className="text-xs text-gray-500">{issue.equipment_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-gray-900">{issue.item_name}</span>
                          {issue.notes && (
                            <span className="text-xs font-bold text-red-600">
                              =&gt; {issue.notes}
                            </span>
                          )}
                          {issue.resolution_notes && (
                            <div className="flex items-center gap-1 text-xs text-black font-bold">
                              <span>{issue.resolution_notes}</span>
                              <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-center">
                          {issue.first_report_photo_url ? (
                            <div 
                              className="w-24 h-24 rounded border overflow-hidden cursor-pointer hover:ring-2 hover:ring-brand-green transition-all"
                              onClick={() => setSelectedImage(issue.first_report_photo_url)}
                            >
                              <img 
                                src={issue.first_report_photo_url} 
                                alt="Ảnh từ báo cáo"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-24 h-24 rounded border bg-gray-100 flex items-center justify-center">
                              <ImageIcon className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-col">
                          <span className="text-gray-900">
                            {new Date(issue.report_date).toLocaleDateString('vi-VN')}
                          </span>
                          <span className="text-xs text-gray-500">{issue.inspector_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-center">
                          {issue.is_resolved ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Image Preview Dialog */}
            <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
              <DialogContent className="max-w-4xl">
                <div className="flex items-center justify-center">
                  <img 
                    src={selectedImage || ''} 
                    alt="Preview"
                    className="max-w-full max-h-[80vh] object-contain rounded-lg"
                  />
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </CardContent>
    </Card>
  );
};
